import { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Typography, VenueIcon } from '@weareopenr/component-library';
import { getBrandVenues } from 'src/api/getBrandVenues';
import { IBrandResponse } from 'src/api/getBrands/types';
import { QueryKeys } from 'src/constants/QueryKeys';
import { IBrandVenueAccess } from 'src/types';
import styles from './User.module.scss';

const getInitials = (name: string) => {
  if (name.length < 2) return name.toUpperCase();
  return name[0].toUpperCase() + name[1].toLowerCase();
};

interface IVenueStatus {
  text: string;
  tone: 'positive' | 'muted';
}

const venueCountLabel = (count: number, totalVenues?: number): string => {
  if (totalVenues) return `${count} of ${totalVenues} venues`;
  return count === 1 ? '1 venue' : `${count} venues`;
};

const venueStatus = (
  isWildcard: boolean,
  entry?: IBrandVenueAccess,
  totalVenues?: number,
): IVenueStatus | null => {
  if (isWildcard || entry?.access === 'full') return { text: 'All venues', tone: 'positive' };
  if (entry?.access === 'none') return { text: 'No venue access', tone: 'muted' };
  if (entry?.access === 'specific') {
    const count = entry.venue_ids?.length ?? 0;
    return { text: venueCountLabel(count, totalVenues), tone: 'muted' };
  }
  return null;
};

interface IBrandVenueDetailsProps {
  brand: IBrandResponse;
  entry?: IBrandVenueAccess;
  isWildcard: boolean;
  venueAccessEnabled: boolean;
  onError: (error: Error) => void;
}

export const BrandVenueDetails = ({
  brand,
  entry,
  isWildcard,
  venueAccessEnabled,
  onError,
}: IBrandVenueDetailsProps): ReactElement => {
  const isSpecific = venueAccessEnabled && !isWildcard && entry?.access === 'specific';

  const { data: venuesData } = useQuery({
    queryKey: [QueryKeys.BRAND_VENUES, brand.brand_id],
    queryFn: () => getBrandVenues(brand.brand_id),
    enabled: isSpecific,
    onError,
  });

  const venues = venuesData?.data ?? [];
  const grantedIds = new Set(entry?.venue_ids ?? []);
  const grantedVenues = venues.filter((venue) => grantedIds.has(venue.id));
  const status = venueAccessEnabled
    ? venueStatus(isWildcard, entry, venues.length || undefined)
    : null;

  return (
    <section aria-label={`${brand.name} access`} className={styles['brand-card']}>
      <div className={styles['brand-card-header']}>
        <Avatar
          {...({
            imageUrl: brand.logo_url || undefined,
            initials: brand.logo_url ? '' : getInitials(brand.name),
            variant: 'custom',
            colourVariant: 'blueberry',
          } as any)}
        />
        <Typography size="medium" weight="semi-bold">
          {brand.name}
        </Typography>
        {status && (
          <span
            className={
              status.tone === 'positive'
                ? styles['venue-status-positive']
                : styles['venue-status-muted']
            }
          >
            {status.text}
          </span>
        )}
      </div>
      {isSpecific && grantedVenues.length > 0 && (
        <ul className={styles['venue-rows']}>
          {grantedVenues.map((venue) => (
            <li key={venue.id} className={styles['venue-row']}>
              <VenueIcon size="small" />
              <Typography size="small">{venue.name}</Typography>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
